'use client';

import { useState } from 'react';
import * as Blockly from 'blockly/core';
import { motion } from 'framer-motion';
import { Copy, RefreshCw, Settings, ZoomIn, ZoomOut } from 'lucide-react';
import { REQUIRED_BRIDGE_VERSION, WEB_APP_VERSION } from '@/constants/constants';
import {
    type BlockTheme,
    type DefaultBoard,
    type WhiteSize,
    useBoardEditorConfigStore,
} from '@/stores/useBoardEditorConfigStore';
import { useBoardEditorStore } from '@/stores/useBoardEditorStore';
import { useToastStore } from '@/stores/useToastStore';
import IconAdaptive from '@/components/IconAdaptive';
import Header from '@/components/modal/Header';
import FormSelect from '@/components/ui/form/FormSelect';
import { syncAllBoardBlocks } from '@/(routers)/boards/editor/block/blockFactory';
import { isVersionOlder } from '@/utils/helpers/bridgeVersionHelper';

type Props = { onClose?: () => void };

const BLOCK_THEMES: { value: BlockTheme; label: string }[] = [{ value: 'basic', label: 'ベーシック' }];

const DEFAULT_BOARDS: { value: DefaultBoard; label: string }[] = [
    { value: 'orange', label: 'オレンジ' },
    { value: 'blue', label: 'ブルー' },
];

const WHITE_SIZE_STEPS: WhiteSize[] = ['micro', 'small', 'medium', 'large'];

// 【重要】最近のChromeは「User-Agent Reduction」というプライバシー対策で、
// navigator.userAgentのバージョン部分を常に「メジャーバージョン.0.0.0」に固定して返す
// (実際のバージョンが150.0.7871.187でも"Chrome/150.0.0.0"としか取れない)。診断情報として
// 不十分なため、正確なフルバージョンが必要な場合はUser-Agent Client Hints API
// (navigator.userAgentData)を使う。このAPIはChromium系(Chrome/Edge、本サービスの推奨
// ブラウザ)のみ対応のため、非対応環境では取得を諦めてnullを返し、呼び出し側で
// navigator.userAgentの表示のみにフォールバックする。
//
// 併せてWindows 10/11の判定にも同じAPIを使う。navigator.userAgent側のOS表記は
// Windows 11でも"Windows NT 10.0"のままカーネルバージョンが更新されないため、
// UA文字列だけではWindows 10/11を区別できない。Client Hintsのplatformmajor
// version(13以上ならWindows 11以降、1〜12ならWindows 10、という仕様)で判定する。
async function getAccurateClientInfo(): Promise<{ browserVersion: string | null; windowsLabel: string | null }> {
    const uaData = (navigator as { userAgentData?: any }).userAgentData;
    if (!uaData?.getHighEntropyValues) return { browserVersion: null, windowsLabel: null };
    try {
        const info = await uaData.getHighEntropyValues(['fullVersionList', 'platformVersion']);
        const brand = info.fullVersionList?.find(
            (b: { brand: string; version: string }) => b.brand === 'Google Chrome' || b.brand === 'Microsoft Edge'
        );
        const browserVersion = brand ? `${brand.brand} ${brand.version}` : null;

        let windowsLabel: string | null = null;
        if (uaData.platform === 'Windows' && info.platformVersion) {
            const major = Number.parseInt(info.platformVersion.split('.')[0], 10);
            if (!Number.isNaN(major)) {
                windowsLabel = major >= 13 ? 'Windows 11' : major > 0 ? 'Windows 10' : 'Windows 10より前';
            }
        }

        return { browserVersion, windowsLabel };
    } catch {
        return { browserVersion: null, windowsLabel: null };
    }
}

export default function BoardEditorSettings({ onClose }: Props) {
    const {
        isAIWhiteEnabled,
        toggleAIWhite,
        blockTheme,
        setBlockTheme,
        whiteSize,
        setWhiteSize,
        defaultBoard,
        setDefaultBoard,
    } = useBoardEditorConfigStore();
    const showToastAsync = useToastStore((s) => s.showToastAsync);

    const {
        bridgeVersion,
        latestBridgeVersion,
        isBridgeConnected,
        isWebSocketConnected,
        osType,
        board,
        port,
        detectedBoards,
        boardIdentity,
        chipId,
        triggerBridgeUpdateCheck,
        isCheckingBridgeUpdate,
        bridgeUpdateCheckMessage,
    } = useBoardEditorStore();
    const [copyFeedback, setCopyFeedback] = useState(false);
    // Chromebookは常時Web Serial API、それ以外はブリッジ未接続時にフォールバックする
    // (BoardToolbar.tsxのuseWebSerialPathと同じ判定基準)。
    const useWebSerialPath = osType === 'chromebook' || !isBridgeConnected;
    // ブリッジから実際の最新バージョンを取得できればそれを使い、古いブリッジ(未対応)で
    // 取得できない間だけ固定値にフォールバックする。
    const effectiveRequiredVersion = latestBridgeVersion ?? REQUIRED_BRIDGE_VERSION;
    const isBridgeUpToDate = !!bridgeVersion && !isVersionOlder(bridgeVersion, effectiveRequiredVersion);

    const sliderValue = Math.max(0, WHITE_SIZE_STEPS.indexOf(whiteSize));

    const handleDefaultBoardChange = (board: DefaultBoard) => {
        setDefaultBoard(board);
        useBoardEditorStore.getState().setBoard(board);
        const ws = Blockly.getMainWorkspace() as Blockly.WorkspaceSvg | null;
        if (ws) syncAllBoardBlocks(ws, board, true);
    };

    const handleCopyDiagnostics = async () => {
        const { browserVersion: accurateBrowserVersion, windowsLabel } = await getAccurateClientInfo();
        const boardsText =
            detectedBoards.length > 0
                ? detectedBoards
                      .map((b) => `  - ${b.port} / ${b.type}${b.is_bluetooth ? ` / BT: ${b.bluetooth_name ?? ''}` : ''}`)
                      .join('\n')
                : '  (検出なし)';
        const lines = [
            '=== SchooMy ブロックエディター 診断情報 ===',
            `取得日時: ${new Date().toLocaleString('ja-JP')}`,
            `Webバージョン: ${WEB_APP_VERSION}`,
            `ブリッジバージョン: ${bridgeVersion ?? '未接続'} (最新: ${latestBridgeVersion ?? `不明・固定値${REQUIRED_BRIDGE_VERSION}を仮使用`})`,
            // OS情報は下の「OS種別」行と重複し、UA文字列の残り(Mozilla/5.0やKHTML, like Gecko等)は
            // 常に同一で情報価値が無いため、正確なバージョンが取れた場合はそれだけを表示する。
            // 非対応ブラウザ(Chrome/Edge以外)では取得できないため、生のUA文字列にフォールバックする。
            `ブラウザ: ${accurateBrowserVersion ?? navigator.userAgent}`,
            `接続方式: ${useWebSerialPath ? 'Web Serial API(ブラウザ直結)' : 'ブリッジ経由'}`,
            `ブリッジ接続: ${isBridgeConnected ? '接続済み' : '未接続'}`,
            `クラウド接続: ${isWebSocketConnected ? '接続済み' : '未接続'}`,
            `OS種別: ${windowsLabel ?? osType}`,
            `選択中ボード: ${board}`,
            `ポート: ${port ?? '(未接続)'}`,
            `chip_id: ${chipId ?? '(未解決)'}`,
            `ボードコード(cd): ${boardIdentity?.cd ?? '(未解決)'}`,
            `hub所属(hub_type_cd): ${boardIdentity?.hub_type_cd ?? '(未解決)'}`,
            `検出ボード一覧(${detectedBoards.length}件):`,
            boardsText,
        ];
        try {
            await navigator.clipboard.writeText(lines.join('\n'));
            setCopyFeedback(true);
            setTimeout(() => setCopyFeedback(false), 2000);
        } catch {
            // 権限拒否・非セキュアコンテキスト(HTTP/iframe等)ではrejectするため、
            // unhandled rejectionのまま無言で終わらせずユーザーにフィードバックする。
            await showToastAsync('クリップボードへのコピーに失敗しました', 'error', 3000);
        }
    };

    const handleZoomOut = () => {
        if (!isAIWhiteEnabled) return;
        const next = sliderValue - 1;
        if (next >= 0) setWhiteSize(WHITE_SIZE_STEPS[next]);
    };

    const handleZoomIn = () => {
        if (!isAIWhiteEnabled) return;
        const next = sliderValue + 1;
        if (next < WHITE_SIZE_STEPS.length) setWhiteSize(WHITE_SIZE_STEPS[next]);
    };

    return (
        <div className="w-full h-full overflow-y-auto p-4">
            <div className="flex flex-row w-full h-12">
                <div className="flex justify-end w-3/4">
                    <Header title="SETTINGS" subTitle="" icon={Settings} />
                </div>
            </div>

            <div className="flex flex-col w-full px-2">
                {/* テーマ */}
                <div className="relative mt-6 pb-1">
                    <div className="absolute -top-[14px] left-3 rounded-md px-3 py-0.5 flex items-center justify-center bg-color-base">
                        <span className="font-cantarell font-bold text-xs text-white">ブロックテーマ</span>
                    </div>
                    <div className="rounded-2xl px-5 py-6" style={{ border: '1.5px solid rgba(0,0,0,0.10)' }}>
                        <div className="flex items-center justify-between">
                            <span className="font-notojp text-xs text-gray-500">
                                ブロックエディターのテーマを切り替えられます。
                            </span>
                            <FormSelect
                                id="block_theme"
                                name="block_theme"
                                value={blockTheme}
                                onChange={(e) => setBlockTheme(e.target.value as BlockTheme)}
                                options={BLOCK_THEMES}
                                wrapperClassName="w-60"
                            />
                        </div>
                    </div>
                </div>

                {/* スクーミーボード */}
                <div className="relative mt-8 pb-1">
                    <div className="absolute -top-[14px] left-3 rounded-md px-3 py-0.5 flex items-center justify-center bg-color-base">
                        <span className="font-cantarell font-bold text-xs text-white">スクーミーボード</span>
                    </div>
                    <div className="rounded-2xl px-5 py-6" style={{ border: '1.5px solid rgba(0,0,0,0.10)' }}>
                        <div className="flex items-center justify-between">
                            <span className="font-notojp text-xs text-gray-500">
                                スクーミーボードのデフォルトカラーを切り替えられます。
                            </span>
                            <FormSelect
                                id="default_board"
                                name="default_board"
                                value={defaultBoard}
                                onChange={(e) => handleDefaultBoardChange(e.target.value as DefaultBoard)}
                                options={DEFAULT_BOARDS}
                                wrapperClassName="w-60"
                            />
                        </div>
                    </div>
                </div>

                {/* ホワイト先生 */}
                <div className="relative mt-8 pb-1">
                    <div className="absolute -top-[14px] left-3 rounded-md px-3 py-0.5 flex items-center justify-center bg-color-base">
                        <span className="font-cantarell font-bold text-xs text-white">ホワイト先生</span>
                    </div>
                    <div className="rounded-2xl px-5 py-6" style={{ border: '1.5px solid rgba(0,0,0,0.10)' }}>
                        <div className="flex flex-col gap-7">
                            {/* ON/OFF行 */}
                            <div className="flex items-center justify-between">
                                <span className="font-notojp text-xs text-gray-500">
                                    AIアシスタント機能のON / OFFが切り替えられます。
                                </span>
                                <label className="relative inline-flex items-center cursor-pointer ml-4 flex-shrink-0">
                                    <input
                                        type="checkbox"
                                        className="sr-only peer"
                                        checked={isAIWhiteEnabled}
                                        onChange={toggleAIWhite}
                                    />
                                    <div
                                        className="w-10 h-6 rounded-full peer bg-gray-200
                                        peer-checked:after:translate-x-4 peer-checked:after:border-white
                                        after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-color-white-base after:border-gray-300
                                        after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-color-ide-setting-toggle"
                                    />
                                </label>
                            </div>

                            {/* サイズ行 */}
                            <div className="flex items-center justify-between">
                                <span className="font-notojp text-xs text-gray-500">
                                    ホワイト先生の表示サイズを切り替えられます。
                                </span>
                                <div className="flex items-center gap-2 ml-4 flex-shrink-0 w-60">
                                    <button
                                        onClick={handleZoomOut}
                                        disabled={!isAIWhiteEnabled || sliderValue === 0}
                                        className="disabled:opacity-30 disabled:cursor-not-allowed transition-opacity"
                                    >
                                        <IconAdaptive
                                            Icon={ZoomOut}
                                            strokeWidth={1.5}
                                            baseSize={24}
                                            className="text-color-co-gray-dark"
                                        />
                                    </button>
                                    <input
                                        type="range"
                                        min={0}
                                        max={WHITE_SIZE_STEPS.length - 1}
                                        step={1}
                                        value={sliderValue}
                                        disabled={!isAIWhiteEnabled}
                                        onChange={(e) => setWhiteSize(WHITE_SIZE_STEPS[Number(e.target.value)])}
                                        className="w-full accent-color-ide-setting-toggle disabled:opacity-30 cursor-pointer disabled:cursor-not-allowed"
                                    />
                                    <button
                                        onClick={handleZoomIn}
                                        disabled={!isAIWhiteEnabled || sliderValue === WHITE_SIZE_STEPS.length - 1}
                                        className="disabled:opacity-30 disabled:cursor-not-allowed transition-opacity"
                                    >
                                        <IconAdaptive
                                            Icon={ZoomIn}
                                            strokeWidth={1.5}
                                            baseSize={24}
                                            className="text-color-co-gray-dark"
                                        />
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* バージョン情報 */}
                <div className="relative mt-8 pb-1">
                    <div className="absolute -top-[14px] left-3 rounded-md px-3 py-0.5 flex items-center justify-center bg-color-base">
                        <span className="font-cantarell font-bold text-xs text-white">バージョン情報</span>
                    </div>
                    <div className="rounded-2xl px-5 py-6 flex flex-col gap-4" style={{ border: '1.5px solid rgba(0,0,0,0.10)' }}>
                        <div className="flex items-center justify-between">
                            <span className="font-notojp text-xs text-gray-500">Webバージョン</span>
                            <span className="font-cantarell text-xs text-color-base">{WEB_APP_VERSION}</span>
                        </div>
                        <div className="flex items-center justify-between">
                            <span className="font-notojp text-xs text-gray-500">ブリッジバージョン</span>
                            <div className="flex flex-col items-end gap-1">
                                <span
                                    className={`font-cantarell text-xs ${
                                        bridgeVersion && !isBridgeUpToDate ? 'text-red-500' : 'text-color-base'
                                    }`}
                                >
                                    {bridgeVersion ?? '未接続'}
                                </span>
                                {bridgeVersion &&
                                    (isBridgeUpToDate ? (
                                        // BoardToolbar.tsx等の「接続中」ラベルと同じ、緑ドット+テキストの
                                        // 見た目に揃え、実バージョンの下に寄せて表示する。
                                        <div className="flex items-center gap-1.5">
                                            <motion.div
                                                className="w-1.5 h-1.5 rounded-full bg-color-ide-board-connected"
                                                animate={{ opacity: [1, 0.3, 1] }}
                                                transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
                                            />
                                            <span className="font-cantarell text-xs text-color-ide-board-connected">
                                                最新版がインストールされています
                                            </span>
                                        </div>
                                    ) : (
                                        <div className="flex items-center gap-1.5">
                                            <div className="w-1.5 h-1.5 rounded-full bg-red-500" />
                                            <span className="font-notojp text-xs text-red-500">
                                                古いバージョンです。更新をおすすめします。
                                            </span>
                                        </div>
                                    ))}
                            </div>
                        </div>
                        {bridgeVersion && !isBridgeUpToDate && (
                            <div className="flex justify-end">
                                <button
                                    onClick={() => triggerBridgeUpdateCheck?.()}
                                    disabled={isCheckingBridgeUpdate || !triggerBridgeUpdateCheck}
                                    className="flex items-center gap-1.5 rounded-md px-3 py-1.5 bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex-shrink-0"
                                >
                                    <IconAdaptive
                                        Icon={RefreshCw}
                                        strokeWidth={2.0}
                                        baseSize={14}
                                        className="text-color-co-gray-dark"
                                    />
                                    <span className="font-cantarell text-xs text-color-co-gray-dark">
                                        {isCheckingBridgeUpdate ? '確認中...' : '今すぐ更新を確認'}
                                    </span>
                                </button>
                            </div>
                        )}
                        {bridgeUpdateCheckMessage && (
                            <div className="flex justify-end">
                                <span className="font-notojp text-[11px] text-gray-500">
                                    {bridgeUpdateCheckMessage}
                                </span>
                            </div>
                        )}
                        <div className="flex items-center justify-between">
                            <span className="font-notojp text-xs text-gray-500">
                                サポートへの問い合わせ時、この情報をコピーして共有してください。
                            </span>
                            <button
                                onClick={handleCopyDiagnostics}
                                className="flex items-center gap-1.5 rounded-md px-3 py-1.5 bg-gray-100 hover:bg-gray-200 transition-colors flex-shrink-0 ml-4"
                            >
                                <IconAdaptive Icon={Copy} strokeWidth={2.0} baseSize={14} className="text-color-co-gray-dark" />
                                <span className="font-cantarell text-xs text-color-co-gray-dark">
                                    {copyFeedback ? 'コピーしました' : '診断情報をコピー'}
                                </span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
